import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PortfolioTotals } from '@/types/franchise';
import { Building2, Users, Star, MessageSquare, Clock, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';

interface PortfolioHealthCardProps {
  totals: PortfolioTotals;
  loading?: boolean;
}

interface StatItemProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  iconColor: string;
  iconBg: string;
}

const StatItem: React.FC<StatItemProps> = ({ icon: Icon, label, value, subValue, iconColor, iconBg }) => {
  const isMobile = useIsMobile();

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 bg-white dark:bg-card rounded-lg border",
      isMobile ? "flex-col text-center" : ""
    )}>
      <div className={cn("p-2 rounded-lg", iconBg)}>
        <Icon className={cn("h-5 w-5", iconColor)} />
      </div>
      <div className={isMobile ? "space-y-0.5" : ""}>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={cn("font-bold", isMobile ? "text-xl" : "text-2xl")}>{value}</p>
        {subValue && (
          <p className="text-xs text-muted-foreground">{subValue}</p>
        )}
      </div>
    </div>
  );
};

const PortfolioHealthCard: React.FC<PortfolioHealthCardProps> = ({ totals, loading }) => {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className={cn(
            "grid gap-4",
            isMobile ? "grid-cols-2" : "grid-cols-3 lg:grid-cols-6"
          )}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const stats: StatItemProps[] = [
    {
      icon: Building2,
      label: 'Total Venues',
      value: totals.venueCount,
      subValue: `${totals.activeVenueCount} open now`,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100 dark:bg-blue-950',
    },
    {
      icon: Star,
      label: 'Avg Rating',
      value: totals.averageRating > 0 ? totals.averageRating.toFixed(1) : '-',
      subValue: 'across all venues',
      iconColor: 'text-yellow-600',
      iconBg: 'bg-yellow-100 dark:bg-yellow-950',
    },
    {
      icon: Users,
      label: 'Total Employees',
      value: totals.employeeCount,
      subValue: 'in portfolio',
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100 dark:bg-green-950',
    },
    {
      icon: MessageSquare,
      label: 'Total Reviews',
      value: totals.reviewCount,
      subValue: 'all time',
      iconColor: 'text-primary',
      iconBg: 'bg-primary/10',
    },
    {
      icon: Clock,
      label: 'Hours This Week',
      value: totals.totalHoursThisWeek || '-',
      subValue: 'scheduled',
      iconColor: 'text-orange-600',
      iconBg: 'bg-orange-100 dark:bg-orange-950',
    },
    {
      icon: CheckCircle2,
      label: 'Shifts This Week',
      value: totals.shiftsThisWeek || '-',
      subValue: 'across venues',
      iconColor: 'text-teal-600',
      iconBg: 'bg-teal-100 dark:bg-teal-950',
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          Portfolio Health
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn(
          "grid gap-4",
          isMobile ? "grid-cols-2" : "grid-cols-3 lg:grid-cols-6"
        )}>
          {stats.map((stat, index) => (
            <StatItem key={index} {...stat} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioHealthCard;
